import { Button, Checkbox, Chip, Spinner } from "@heroui/react";

import HintTooltip from "@/components/common/HintTooltip";
import { ArrowUpRight, FileWarning, ImageOff, QrCode, ScanText } from "lucide-react";
import { useState } from "react";

import { thumbnailSrc } from "../api";
import type { OcrConfidence } from "../api";
import type { PanelDrawing, PanelItem } from "./types";

/**
 * 図面 1 枚のカード。サムネイル・読み取った図番・紐付け状態の 3 つだけ出す。
 *
 * 一覧でやりたいのは「**どの図面がまだ明細に付いていないか**」を見つけることで、
 * それには絵と図番と状態があれば足りる。取込日や版数まで並べると、肝心の絵が
 * 小さくなって探しにくくなる。詳しい操作はカードを押して開くダイアログで行う。
 *
 * サムネイルは API が実体を返す固定の URL（`thumbnailSrc`）。値が変わらないので
 * 一覧を取り直してもブラウザのキャッシュが効く。それでも読めなかったときは
 * **代替表示に落とす**（`onError`）。壊れた画像アイコンが並ぶのを防ぐ。
 *
 * 明細に紐付いた図面には、**その明細の作業画面への入口**をカードの下端に横いっぱい
 * で出す（見積なら工程ごとの見積画面）。紐付けたあとにやることがある画面では
 * 一番押される導線になる。入口が無い持ち主では `onOpenEditor` を省く。
 *
 * 図番の横に**読み取りの確度**を出す。確度を出さないと「読めて図番に入ったもの」と
 * 「読めたが入らなかったもの」の差が利用者から見えず、どのカードを確かめればよいか
 * 分からない。`low` は図番に入っていないので、読んだ値もあわせて出す。
 *
 * **根拠の数値（2 位とのスコア差）は一覧には出さない**。数字だけ並べられても何と
 * 比べればよいか分からず、42 枚並ぶカードの情報量だけが増える。見たいときは
 * カードを開く。
 *
 * **複数選択できる**。一括で明細を起こしたり消したりするのに、1 枚ずつ開いて
 * いられないため。選択の印はサムネイルの左上に重ねる（カードの中に行を足すと、
 * 肝心の絵が小さくなる）。
 *
 * カードを押したときの動きは、**何かが選ばれているかどうかで変わる**。何も選ばれて
 * いなければ詳細を開き、選択中なら選択を足す。選択中も詳細が開く形にすると、
 * 2 枚目以降は小さなチェックボックスを狙い続けることになる。
 *
 * 入れ子のボタンにならないよう、カードの外枠は button ではなく div にしてある。
 */
export default function DrawingCard({
  drawing,
  isGenerating,
  isSelected,
  selectable,
  selecting,
  onToggleSelect,
  onOpen,
  onOpenEditor,
  editorLabel,
  linkedItem,
}: {
  drawing: PanelDrawing;
  /**
   * いま生成を待っている最中か。
   *
   * ``pending`` だけで「生成中」と出すと、誰も作りにいかない古い版が永遠に
   * 回り続ける。待っているのは自分が仕掛けたぶんだけ。
   */
  isGenerating: boolean;
  /** 一括操作の対象に選ばれているか。 */
  isSelected: boolean;
  /** 選べるか（書き込み権限が無いときは選ばせない）。 */
  selectable: boolean;
  /**
   * いま一覧で何かが選ばれているか。
   *
   * **選び始めたらカードを押すと選択が増える**（詳細は開かない）。1 枚目だけ
   * チェックボックスを狙わせて 2 枚目からカード全体で選べる、という形にしないと
   * 枚数が多いときに手間が増える。詳細を開きたいときは選択を外してから押す。
   */
  selecting: boolean;
  onToggleSelect: (drawing: PanelDrawing) => void;
  onOpen: (drawing: PanelDrawing) => void;
  /**
   * 紐付いた明細の作業画面へ移る。**省くと下端のボタンを出さない**
   * （案件には工程ごとの見積画面が無い）。
   */
  onOpenEditor?: (drawing: PanelDrawing) => void;
  /** そのボタンの文言。 */
  editorLabel?: string;
  /**
   * 紐付いた明細。**図番の書き方が違うまま付いたかを示す**ために要る
   * （突き合わせは表記を寄せて一致させることがある）。
   */
  linkedItem?: PanelItem;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const linked = drawing.item_id != null;
  // **寄せて付いたもの**。突き合わせは完全一致で決まらないとき、区切りや混同字
  // （``O`` と ``0``）を寄せて探す。人が見れば同じでも、**違う部品の枝番だった**
  // ということが起こりうるので（受領サンプルの「ミラー」）、違いが残っている
  // ことをカードに出して目を通せるようにする。
  const notationDiffers =
    linked &&
    !!drawing.drawing_number &&
    !!linkedItem?.drawing_number &&
    drawing.drawing_number.trim() !== linkedItem.drawing_number.trim();
  const thumbnail = thumbnailSrc(drawing.thumbnail_path);
  const showImage = thumbnail != null && !imageFailed;

  return (
    <div
      className={`relative flex h-full flex-col overflow-hidden rounded-lg border bg-white transition-shadow hover:shadow-md ${
        isSelected ? "border-brand-500 ring-2 ring-brand-500" : "border-default-200"
      }`}
    >
      {selectable && (
        // 絵に重ねる。カード全体が「開く」なので、ここでの押下は伝播させない。
        <div
          className="absolute top-1.5 left-1.5 z-10 rounded-md bg-white/85 px-1"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            size="sm"
            isSelected={isSelected}
            onValueChange={() => onToggleSelect(drawing)}
            aria-label={`${drawing.drawing_number || "図番なし"} を選択`}
            classNames={{ base: "m-0 p-0", label: "hidden" }}
          />
        </div>
      )}
      <button
        type="button"
        data-testid="drawing-card-body"
        onClick={() => (selecting ? onToggleSelect(drawing) : onOpen(drawing))}
        aria-label={
          selecting
            ? `${drawing.drawing_number || "図番なし"} を選択`
            : `${drawing.drawing_number || "図番なし"} の図面を開く`
        }
        className="cursor-pointer text-left focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-500"
      >
        {/* 図面は A3 横が多い。縦横比を固定して並びを揃え、絵は contain で収める
            （cover にすると表題欄が切れて、図番が読めなくなる）。 */}
        <div className="flex aspect-4/3 items-center justify-center bg-default-50">
          {showImage ? (
            <img
              src={thumbnail ?? ""}
              alt=""
              loading="lazy"
              onError={() => setImageFailed(true)}
              className="size-full object-contain"
            />
          ) : (
            <ThumbnailPlaceholder
              drawing={drawing}
              isGenerating={isGenerating}
              failedToLoad={imageFailed}
            />
          )}
        </div>

        <span className="block truncate px-3 pt-2 text-sm font-bold text-default-700">
          {drawing.drawing_number || (
            <span className="font-normal text-default-400">
              {drawing.ocr_status === "pending" && isGenerating
                ? "図番を読み取り中"
                : "図番なし"}
            </span>
          )}
        </span>

        <OcrHint drawing={drawing} />
      </button>

      <div className="flex flex-wrap items-center gap-1 px-3 pt-1 pb-2">
        {linked ? (
          <Chip size="sm" variant="flat" color="success">
            {drawing.item_sequence} 行目に紐付済
          </Chip>
        ) : null}
        {notationDiffers && (
          <HintTooltip
            content={`図面の図番「${drawing.drawing_number}」と明細の図番「${linkedItem?.drawing_number}」は書き方が違います。表記を寄せて突き合わせました。別の部品でないか確かめてください。`}
          >
            {/* ツールチップは hover と focus で開くので、当たり判定を持つ要素で
                包む（一覧の「見積がない案件」の印と同じ作り）。 */}
            <span
              tabIndex={0}
              role="img"
              // 色と形だけでは伝わらないので、読み上げにも理由を乗せる。
              aria-label={`図番の表記が明細と違います（明細は ${linkedItem?.drawing_number}）`}
              className="inline-flex rounded outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            >
              <Chip size="sm" variant="flat" color="warning">
                表記ちがい
              </Chip>
            </span>
          </HintTooltip>
        )}
        {!linked && (
          <Chip size="sm" variant="flat" color="warning">
            紐付無し
          </Chip>
        )}
        <OcrConfidenceChip confidence={drawing.ocr_confidence} />
        {/* 現場へ出せる紙ができているか。**版表記では分からない**（客先の版表記と
            同じ採番を使う）ので、カードで示す。 */}
        {drawing.qr_stamped && (
          <Chip
            size="sm"
            variant="flat"
            color="success"
            startContent={<QrCode size={12} />}
          >
            QR
          </Chip>
        )}
        {/* まとめて押した QR の進み具合。**済めば消える**ので、出ている間は
            「まだ紙が出来ていない」を意味する。 */}
        {drawing.qr_queue_status === "pending" && (
          <Chip size="sm" variant="flat" color="default">
            QR 押印待ち
          </Chip>
        )}
        {drawing.qr_queue_status === "failed" && (
          <Chip size="sm" variant="flat" color="danger">
            QR 押印に失敗
          </Chip>
        )}
        {/* 見積と案件は**同じ 1 枚を共有している**。共有している図面はこの画面
            からは動かせないことがある（API が断る）ので、選ぶ前に分かるように
            出どころをここに出す。 */}
        {drawing.origin_note && (
          <Chip size="sm" variant="flat" color="primary">
            {drawing.origin_note}
          </Chip>
        )}
      </div>

      {/* 見積画面への入口。紐付けたあとにやることは金額を入れることなので、
          この画面で一番押される導線として下端に横いっぱいで置く。
          mt-auto で、同じ行のカードの高さが揃っていても下端に貼り付く。 */}
      {linked && onOpenEditor && (
        <div className="mt-auto border-t border-default-100 p-2">
          <Button
            fullWidth
            size="sm"
            radius="sm"
            color="primary"
            aria-label={`${drawing.item_sequence} 行目の${editorLabel ?? "画面を開く"}`}
            startContent={<ArrowUpRight size={16} />}
            onPress={() => onOpenEditor(drawing)}
          >
            {editorLabel ?? "開く"}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * OCR の読みが図番と食い違うときだけ出す小さな手がかり。
 *
 * **採用されなかった読みを捨てない**ために出す。図番が既に入っている図面には
 * OCR は書き込まないので、読みが違っていてもこれが無いと誰も気づけない。
 * 一致しているときに出しても情報が増えないので黙っている。
 */
function OcrHint({ drawing }: { drawing: PanelDrawing }) {
  const read = drawing.ocr_drawing_number;
  if (!read || read === drawing.drawing_number) return null;
  return (
    <span className="flex items-center gap-1 px-3 pt-0.5 text-tiny text-default-400">
      <ScanText size={12} className="shrink-0" />
      <span className="truncate">OCR: {read}</span>
    </span>
  );
}

/** 読み取りの確度。読み取っていなければ何も出さない。 */
function OcrConfidenceChip({ confidence }: { confidence: OcrConfidence | null }) {
  if (!confidence) return null;
  // high を目立たせない（ほとんどがこれになる）。目で確かめてほしいのは
  // medium と low のほうなので、そちらだけ色を付ける。
  const { label, color } = {
    high: { label: "OCR確度 高", color: "default" },
    medium: { label: "OCR確度 中", color: "warning" },
    low: { label: "OCR確度 低", color: "warning" },
  }[confidence] as { label: string; color: "default" | "warning" };
  return (
    <Chip size="sm" variant="flat" color={color}>
      {label}
    </Chip>
  );
}

/** サムネイルが出せないときの表示。**なぜ出せないのかで文言を変える**。 */
function ThumbnailPlaceholder({
  drawing,
  isGenerating,
  failedToLoad,
}: {
  drawing: PanelDrawing;
  isGenerating: boolean;
  failedToLoad: boolean;
}) {
  if (drawing.thumbnail_status === "pending" && isGenerating && !failedToLoad) {
    return <Spinner size="sm" label="生成中" />;
  }
  if (drawing.thumbnail_status == null) {
    return (
      <div className="flex flex-col items-center gap-1 text-default-400">
        <ImageOff size={28} />
        <span className="text-tiny">PDF なし</span>
      </div>
    );
  }
  // pending のまま待ち時間を過ぎたものは「未生成」。作り直しで拾える。
  const label = failedToLoad
    ? "読み込めません"
    : drawing.thumbnail_status === "pending"
      ? "未生成"
      : "サムネイルなし";
  return (
    <div className="flex flex-col items-center gap-1 text-default-400">
      <FileWarning size={28} />
      <span className="text-tiny">{label}</span>
    </div>
  );
}
